import { useCallback, useEffect, useState } from 'react';
import { Button } from '../components/Button';
import { Body, Screen, ScreenBody, Title } from '../components/Screen';
import { copy } from '../copy';
import { env } from '../config/env';
import { links } from '../assets';
import { shareFromUrl } from '../lib/saveClip';

/**
 * The retrieval page: /clip/<session id>, which is where the SMS link lands.
 *
 * Shape follows the MHA share page. Three states that matter, and the difference
 * between them is the whole point:
 *
 *   ready    — the transcode finished. Play it, and offer to save it.
 *   pending  — no row yet, or not transcoded yet. On show night this is usually
 *              a kiosk still draining its upload queue, not a bad link, so it
 *              says "check back" and offers a retry rather than "not found".
 *   failed   — the transcode failed. Say so plainly instead of leaving them
 *              refreshing a page that will never change.
 *
 * Nothing here is behind a login. The session id in the link is the capability —
 * same trust model as texting a signed URL, which is what this replaces.
 */

type ClipState =
  | { state: 'loading' }
  | { state: 'ready'; playUrl: string; downloadUrl: string; filename: string }
  | { state: 'pending' }
  | { state: 'failed' }
  | { state: 'error' };

export function ClipScreen({
  sessionId,
  shareCode,
}: {
  /** One of the two is always set: the SMS link carries an id, a code carries a code. */
  sessionId?: string;
  shareCode?: string;
}) {
  const [result, setResult] = useState<ClipState>({ state: 'loading' });
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const load = useCallback(async () => {
    setResult({ state: 'loading' });
    try {
      const query = shareCode
        ? `c=${encodeURIComponent(shareCode)}`
        : `s=${encodeURIComponent(sessionId ?? '')}`;
      const res = await fetch(`${env.supabaseUrl}/functions/v1/get-clip?${query}`, {
        headers: { apikey: env.supabaseAnonKey },
      });
      const body = (await res.json()) as ClipState & { state: string };
      if (body.state === 'ready' || body.state === 'pending' || body.state === 'failed') {
        setResult(body as ClipState);
      } else {
        setResult({ state: 'error' });
      }
    } catch {
      setResult({ state: 'error' });
    }
  }, [sessionId, shareCode]);

  useEffect(() => {
    void load();
  }, [load]);

  async function save() {
    if (result.state !== 'ready' || busy) return;
    setBusy(true);
    try {
      const outcome = await shareFromUrl(result.playUrl, result.downloadUrl, result.filename);
      // On iOS a direct download lands in Files, not Photos, and the guest is
      // left wondering where it went. Say where to look.
      if (outcome === 'downloaded') setNote(copy.clip.savedNote);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Screen>
      <div className="pt-10" />

      {result.state === 'ready' ? (
        <>
          <Title>{copy.clip.readyHeading}</Title>
          <div className="flex min-h-48 w-full flex-1 items-center justify-center py-4">
            {/*
              muted so the browser actually honours autoPlay — there is no user
              gesture on a page opened from a text, and an unmuted autoplay is
              simply blocked, leaving a dead first frame. Controls are on so they
              can unmute, which matters here: the caw is the point.
            */}
            <video
              src={result.playUrl}
              controls
              autoPlay
              loop
              muted
              playsInline
              className="aspect-[9/16] h-full max-h-full w-auto max-w-full rounded-2xl bg-black object-cover"
            />
          </div>
          <div className="w-full max-w-sm space-y-3 pb-6">
            <Button onClick={() => void save()} disabled={busy}>
              {busy ? copy.clip.saving : copy.clip.save}
            </Button>
            {note ? <p className="text-center text-sm text-bone/60">{note}</p> : null}
            <a
              href={links.tickets}
              className="block pt-1 text-center text-sm text-bone/60 underline"
            >
              {copy.endCard.tickets}
            </a>
          </div>
        </>
      ) : (
        <>
          <ScreenBody>
            <Title>{headingFor(result.state)}</Title>
            <Body>{bodyFor(result.state)}</Body>
          </ScreenBody>
          <div className="w-full max-w-sm space-y-3 pb-6">
            {result.state === 'pending' || result.state === 'error' ? (
              <Button onClick={() => void load()}>{copy.clip.retry}</Button>
            ) : null}

            {/*
              Code path only: an SMS link cannot be mistyped, so offering this
              there would only invite a guest with a perfectly good link to go
              looking for a code they were never given.
            */}
            {shareCode && result.state !== 'loading' ? (
              <a href="/c" className="block pt-1 text-center text-sm text-bone/60 underline">
                {copy.clip.wrongCode}
              </a>
            ) : null}
          </div>
        </>
      )}
    </Screen>
  );
}

function headingFor(state: ClipState['state']): string {
  if (state === 'loading') return copy.clip.loadingHeading;
  if (state === 'pending') return copy.clip.pendingHeading;
  if (state === 'failed') return copy.clip.failedHeading;
  return copy.clip.errorHeading;
}

function bodyFor(state: ClipState['state']): string {
  if (state === 'loading') return copy.clip.loadingBody;
  if (state === 'pending') return copy.clip.pendingBody;
  if (state === 'failed') return copy.clip.failedBody;
  return copy.clip.errorBody;
}
